import React, { FC } from "react"

interface PreferredSlot {
  date: string
  timeSlot: string
  timeSlotLabel: string
  isFixed: boolean
}

interface Booking {
  id: string
  type: string
  contractorName: string
  address: string
  status: string
  statusColor: string
  preferredSlots: PreferredSlot[]
  appliedDatetime: string
  bookingId: number
}

interface BookingTableProps {
  bookings?: Booking[]
  perPage?: number
  totalCount?: number
  currentPage?: number
  onRowClick?: (bookingId: number) => void
  onPerPageChange?: (perPage: number) => void
}

const BookingTable: FC<BookingTableProps> = ({ 
  bookings = [], 
  perPage = 10,
  totalCount = 0,
  currentPage = 1,
  onRowClick,
  onPerPageChange 
}) => {
  // 表示件数の範囲を計算
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * perPage + 1
  const endItem = Math.min(currentPage * perPage, totalCount)
  const getStatusClass = (color: string) => {
    const colorMap: Record<string, string> = {
      yellow: 'bg-status-pending/20 text-status-pending',
      green: 'bg-status-confirmed/20 text-status-confirmed',
      blue: 'bg-status-call-scheduled/20 text-status-call-scheduled',
      red: 'bg-status-cancelled/20 text-status-cancelled'
    }
    return colorMap[color] || 'bg-gray-100 text-gray-800'
  }

  return (
    <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900 m-0">予約申請一覧</h3>
          <div className="flex items-center gap-2">
            <label htmlFor="perPageSelect" className="text-sm">
              表示件数：{perPage}件 / {totalCount.toLocaleString()}件中
            </label>
            <select
              id="perPageSelect"
              value={perPage}
              onChange={(e) => onPerPageChange?.(Number(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="5">5件</option>
              <option value="10">10件</option>
              <option value="20">20件</option>
              <option value="50">50件</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border-t border-gray-200 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                予約ID
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                種類
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                契約者氏名
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                住所
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                ステータス
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                希望日
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors">
                申請日時
              </th>
              <th className="bg-gray-50 px-4 py-4 text-left font-semibold text-gray-900 border-b border-gray-200 whitespace-nowrap">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  データがありません
                </td>
              </tr>
            ) : (
              bookings.map((booking) => (
                <tr
                  key={booking.bookingId}
                  className="transition-colors hover:bg-primary-light/10 cursor-pointer"
                  onClick={() => onRowClick?.(booking.bookingId)}
                >
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">{booking.id}</td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">{booking.type}</td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">{booking.contractorName}</td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">{booking.address}</td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">
                    <span
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold text-center min-w-[70px] inline-block ${getStatusClass(booking.statusColor)}`}
                    >
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">
                    {booking.preferredSlots && booking.preferredSlots.length > 0 ? (
                      <div className="space-y-1">
                        {booking.preferredSlots.map((slot, index) => (
                          <div key={index} className="text-sm">
                            {slot.date} {slot.timeSlotLabel}
                            {slot.isFixed && (
                              <span className="ml-2 text-xs text-primary font-semibold">（確定）</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle">{booking.appliedDatetime}</td>
                  <td className="px-4 py-4 border-b border-gray-200 align-middle" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onRowClick?.(booking.bookingId)}
                      className="inline-flex items-center justify-center px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium transition-all hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/20 whitespace-nowrap"
                    >
                      詳細
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default BookingTable

